import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import axios from 'axios';
import { db } from '../App';
import { doc, setDoc, getDoc, Timestamp } from "firebase/firestore";
import { Edit, MusicNoteRounded, Save } from '@mui/icons-material';
import './SelectedPlaylist.css'

export interface PlaylistCommentData {
    name: string,
    users: string[],
    tracks: any[],
    last_updated: Timestamp,
    artwork_url: string
}

function getIDFromSpotifyTrack(track: any): string {
    return `${track.track.id}-${track.added_at}-${track.added_by.id}`
}

const UPDATE_THRESHOLD_IN_MILLIS = 5000000

const ARTWORK_SIZE_THRESHOLD_IN_PX: number = 100

/**
 * merge cloud object with updated version
 * 
 * @param playlistCommentData existing cloud stored playlistCommentData
 * @param newItems new list of items from spotify api
 * @param playlistName name of playlist from spotify api
 * @returns updated PlaylistCommentData object containing correct data
 */
function mergeNewPlaylistItemStateWithRecord(playlistCommentData: PlaylistCommentData, newItems: any, playlistName: string, playlistArtworkUrl: string): PlaylistCommentData {
    playlistCommentData.name = playlistName
    const new_users: string[] = []
    const new_track_items: any[] = []
    let tracksMap: any = {}
    playlistCommentData.tracks.forEach((track) => {
        tracksMap[track.id] = track
    })
    newItems.forEach((item: any) => {
        if (!(new_users.includes(item.added_by.id))) {
            new_users.push(item.added_by.id)
        }
        const item_id = getIDFromSpotifyTrack(item)
        let old_track = tracksMap[item_id]
        if (old_track) {
            old_track["image_url"] = getSmallestAcceptableImageURLFromArray(item.track.album.images)
            new_track_items.push(old_track)
        } else {
            new_track_items.push({
                "id": item_id,
                "added_by": item.added_by.id,
                "comments": {},
                "title": item.track.name,
                "artist": item.track.artists[0].name,
                "image_url": getSmallestAcceptableImageURLFromArray(item.track.album.images),
            })
        }
    })
    playlistCommentData.users = new_users
    playlistCommentData.tracks = new_track_items
    playlistCommentData.artwork_url = playlistArtworkUrl
    playlistCommentData.last_updated = Timestamp.now()
    return playlistCommentData
}

/**
 * 
 * 
 * @param imageArray per the Spotify API, an image array sorted in decreasing order of size containing a URL string and height and width numbers.
 */
function getSmallestAcceptableImageURLFromArray(imageArray: Array<any>): string | null {
    if (imageArray.length === 0) { return null; }
    for (let i = imageArray.length - 1; i >= 0; i--) {
        if (imageArray[i].height >= ARTWORK_SIZE_THRESHOLD_IN_PX) {
            return imageArray[i].url
        }
    }
    return imageArray[0].url
}

/** commentList is the local cache of comments for this user. 
 *  This is where user's comments are stored during editing mode.
 *  This is also the source of truth from which user comments are pulled when updating cloud documents (i.e. when Save button is pressed).
 * 
 *  It must be instantiated outside of the SelectedPlaylistView function because otherwise it will keep getting refreshed as empty.
 */
const commentList: string[] = []

let dataFetched: boolean = false;
let isFetching: boolean = false;

// #region MASTER FUNCTION 

export function SelectedPlaylistView() {
    const { playlistId } = useParams()
    const [playlistData, setPlaylistData] = useState<any>(null)
    const [playlistCommentData, setPlaylistCommentData] = useState<any>(null)
    const [editingMode, setEditing] = useState<boolean>(false)

    let access_token = window.localStorage.getItem("token")
    let loggedInUser: string = window.localStorage.getItem("loggedInUser") ?? ""


    const userCanEdit = playlistCommentData !== null && playlistCommentData !== undefined && playlistCommentData.users.includes(loggedInUser)

    // #region BACKEND FUNCTIONS

    const fetchPageOfPlaylistItems = useCallback(async (playlistId: string, offset: number, limit: number = 50): Promise<any> => {
        const playlist_resp: any = await axios.get(`https://api.spotify.com/v1/playlists/${playlistId}/tracks`, {
            headers: {
                Authorization: `Bearer ${access_token}`
            },
            params: {
                limit: `${limit}`,
                offset: `${offset}`
            }
        }).catch(function (error) {
            return
        })
        if (playlist_resp == null) { return }
        return playlist_resp.data
    }, [access_token])

    const fetchAllPlaylistItems = useCallback(async (playlistId: string): Promise<any> => {
        const items = []
        let initialFetch = await fetchPageOfPlaylistItems(playlistId, 0) // fetch first page of 50 tracks
        const totalItems = initialFetch.total   // grab total number of tracks from initial fetch
        items.push(...initialFetch.items) // extend our items list to include the initial fetch

        // iterate over remaining items 50 at a time
        for (let i = 50; i < totalItems; i += 50) {
            const thisFetch = await fetchPageOfPlaylistItems(playlistId, i)
            items.push(...thisFetch.items)
        }

        return items
    }, [fetchPageOfPlaylistItems])

    const fetchPlaylist = async () => {
        if (playlistId === undefined) {
            return
        }
        const playlist_resp: any = await axios.get(`https://api.spotify.com/v1/playlists/${playlistId}`, {
            headers: {
                Authorization: `Bearer ${access_token}`
            },
            params: {
            }
        }).catch(function (error) {
            return
        })
        if (playlist_resp == null) { return }
        setPlaylistData(playlist_resp)
    }

    const getPlaylistCommentData = async (playlistId: string): Promise<PlaylistCommentData | null> => {
        let docRef = doc(db, "playlists", playlistId)
        const playlistDoc = await getDoc(docRef)
        const docData = playlistDoc.data()
        if (docData) {
            let data = {
                name: docData.name,
                users: docData.users,
                tracks: docData.tracks,
                last_updated: docData.last_updated,
                artwork_url: docData.artwork_url ?? ""
            }
            setPlaylistCommentData(data)
            return data
        } else {
            return null
        }
    }

    const setPlaylistCommentDataFromTracks = useCallback(async (tracksList: any) => {
        const users: any[] = [];
        const tracks: any = [];
        tracksList.forEach((item: any) => {
            if (!(users.includes(item.added_by.id))) {
                users.push(item.added_by.id)
            }

            const track_id = getIDFromSpotifyTrack(item)
            tracks.push({
                "id": track_id,
                "added_by": item.added_by.id,
                "comments": {},
                "title": item.track.name,
                "artist": item.track.artists[0].name,
                "image_url": getSmallestAcceptableImageURLFromArray(item.track.album.images),
            })
        })
        const new_document: PlaylistCommentData = {
            name: playlistData.data.name,
            users: users,
            tracks: tracks,
            last_updated: Timestamp.now(),
            artwork_url: playlistData.data.images[0].url ?? ""
        }

        // set the Firestore document
        await setDoc(doc(db, "playlists", playlistData.data.id), new_document)
        // set the React local object
        setPlaylistCommentData(new_document)
    }, [playlistData])

    // Initialize view data
    if (playlistId) {
        getPlaylistCommentData(playlistId).then((playlistCommentData) => {
            if (playlistCommentData === null && access_token !== null) {
                fetchPlaylist() // make spotify API fetch if we don't have stored playlist comment data in firestore
            }
        })
    }

    useEffect(() => {
        // if playlist data loads successfully from spotify...
        if (playlistData !== undefined && playlistData !== null) {
            // if playlist comment data already exists in firestore and hasn't been updated since threshold
            // we have to merge our local data with the firestore data
            if (playlistCommentData !== undefined && playlistCommentData !== null) {
                if (!isFetching && (!dataFetched
                    || Timestamp.now().toMillis() - playlistCommentData.last_updated.toMillis() > UPDATE_THRESHOLD_IN_MILLIS)) {
                    isFetching = true;
                    fetchAllPlaylistItems(playlistData.data.id).then((items) => {
                        let commentDoc = mergeNewPlaylistItemStateWithRecord(playlistCommentData, items, playlistData.data.name, playlistData.data.images[0].url)
                        if (playlistId) {
                            let docRef = doc(db, "playlists", playlistId)
                            setDoc(docRef, commentDoc)
                        }
                        dataFetched = true
                        isFetching = false
                    })

                }
            } else { // otherwise we create a new playlist data object in firestore
                if (playlistId && !isFetching && !dataFetched) {
                    isFetching = true
                    fetchAllPlaylistItems(playlistId).then((tracks) => {
                        setPlaylistCommentDataFromTracks(tracks);
                        dataFetched = false;
                        isFetching = false
                    });
                }
            }
        }
    }, [playlistData, playlistCommentData, playlistId, fetchAllPlaylistItems, setPlaylistCommentDataFromTracks])

    const updateLocalData = (id: string, comment: string, index: number) => {
        commentList[index] = comment
    }

    // #region FRONTEND FUNCTIONS

    function trackView(trackCommentItem: any, index: number) {

        const listItems = []
        let curUserComment = "";
        let curUserHasComment = false;


        for (const userId in trackCommentItem.comments) {
            if (userId === loggedInUser) {
                curUserHasComment = true
                curUserComment = trackCommentItem.comments[userId]
                listItems.push(
                    <li key={userId}>
                        {editingMode
                            ? <div className="Comment-editing" contentEditable="true" onInput={(e) => {
                                const newContent = e.currentTarget.textContent ? e.currentTarget.textContent : ""
                                updateLocalData(trackCommentItem.id, newContent, index)
                            }}>{curUserComment}</div>
                            : <p>{`${trackCommentItem.comments[userId]}`} <br/> <span className="Comment-by">{`- ${userId}`}</span> </p>}
                    </li>
                )
            } else {
                listItems.push(
                    <li key={userId}>
                        <p>{`${trackCommentItem.comments[userId]} - ${userId}`}</p>
                    </li>
                )
            }
        }

        if (!curUserHasComment && editingMode) {
            listItems.push(
                <li key={loggedInUser}>
                    <div className="Comment-editing" contentEditable="true" onInput={(e) => {
                        const newContent = e.currentTarget.textContent ? e.currentTarget.textContent : ""
                        updateLocalData(trackCommentItem.id, newContent, index)
                    }}
                    >{curUserComment}</div>
                </li>
            )
        }
        return (
            <div className="Track">
                {trackCommentItem.image_url ? <img src={trackCommentItem.image_url} className="Track-artwork" alt={`${trackCommentItem.title} artwork`}></img> : <MusicNoteRounded />}
                <p>{`${trackCommentItem.title} by ${trackCommentItem.artist}`}</p>
                <p className= "Added-by">{`added by ${trackCommentItem.added_by}`}</p>
                <ul className="Track-comment-list">
                    {listItems}
                </ul>
            </div>

        )
    }

    function playlistView(playlistCommentData: PlaylistCommentData) {
        if (playlistCommentData == null || isFetching) {
            return <div className="loader"></div>
        }

        const listItems = playlistCommentData.tracks.map((item: {
            id: string,
            comments: any,
            artist: string,
            title: string,
            added_by: string,
            image_url: string,
        }, index: number) =>
            <li key={item.id}>
                <div style={{paddingBottom:"10px"}}>
                    {trackView(item, index)}
                </div>
            </li>
        );

        return (
            <div>
                <a className="SelectedPlaylist-link" href={`https://open.spotify.com/playlist/${playlistId}`} target="_blank" rel="noreferrer">
                    <img src={playlistCommentData.artwork_url} alt="Playlist artwork" className="SelectedPlaylist-artwork" />
                    <p className="SelectedPlaylist-title">{playlistCommentData.name}</p>
                </a>
                <ul className="SelectedPlaylist">
                    {listItems}
                </ul>
            </div>
        )
    }

    const onEditButtonClick = async () => {
        // TODO:    Change to updateDoc for safety
        // TODO:    Refactor to runTransaction including getDoc and subsequent updateDoc 
        //          so that we don't overwrite data changes from elsewhere. 
        if (editingMode) {
            playlistCommentData.last_updated = Timestamp.now()
            if (loggedInUser !== "") {
                for (let i = 0; i < playlistCommentData.tracks.length; i++) {
                    if (commentList[i]) {
                        playlistCommentData.tracks[i].comments[loggedInUser] = commentList[i]
                    }
                }
            }
            if (playlistId) {
                let docRef = doc(db, "playlists", playlistId)
                await setDoc(docRef, playlistCommentData).then(() => { setEditing(!editingMode) })
            }
        } else {
            setEditing(!editingMode)
        }
    }

    return (
        <div className="SelectedPlaylist-background">
            <div style={{ display: "flex", padding: "1vh", justifyContent: "space-between" }}>
                <a className="Back-button" href="/">
                    Back
                </a>
                {(userCanEdit) ?
                    <div className="Edit-button" onClick={onEditButtonClick}>
                        {editingMode ? <Save /> : <Edit />}
                        {editingMode ? <p>Save</p> : <p>Edit</p>}
                    </div> : <></>}
                <div className="Refresh-button" onClick={() => {
                    dataFetched = false
                    fetchPlaylist()
                }}>Refresh Playlist</div>
            </div>

            {playlistView(playlistCommentData)}
        </div>
    )
}